// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Modal, SearchBar } from '@loa/ui-components';
import { proposalApi } from '../../api/proposalApi';
import {
  proposalTypeLabel,
  type Proposal,
  type ProposalType,
} from '../../types/proposal';
import type { Client } from '../../types/client';
import { LOATableCard, TrashIcon, EyeIcon } from '../loa';
import type { LOATableColumn, LOATableAction } from '../loa';
import { formatLastUpdated } from '../requests/requestCells';
import { statusDisplay } from './proposalDisplay';
import { NewProposalButton } from './NewProposalButton';
import { NewPensionProposalModal } from './NewPensionProposalModal';
import { NewFundInvestmentProposalModal } from './NewFundInvestmentProposalModal';
import { NewProtectionProposalModal } from './NewProtectionProposalModal';
// Reuse the Policy-Number cell styling from the Policies table so the
// Proposals tab reads identically to Requests/Policies.
import '../PolicyList.css';
// Scoped overrides that align the Proposals table (LOATableCard) with the
// Requests/Policies tables — header/cell padding + title font.
import './ProposalsTab.css';

/**
 * A DRAFT PENSION proposal born from the pension wizard (has a
 * `sourceQuoteId`). These resume the wizard route on click instead of the
 * flat detail page — the list payload doesn't carry `wizardStep`, so the row
 * shows a generic "Wizard draft" chip rather than "Step n of 4" (the
 * single-proposal GET used by the wizard itself resolves the exact step).
 */
const isWizardDraft = (p: Proposal): boolean =>
  p.proposalType === 'PENSION' && p.status === 'DRAFT' && Boolean(p.sourceQuoteId);

/**
 * A DRAFT BREAKTHROUGH_REPORT proposal — always builder-born (there is no
 * flat-compose fallback for this type). These resume the sectioned builder
 * on click instead of the generic detail page.
 */
const isBreakthroughDraft = (p: Proposal): boolean =>
  p.proposalType === 'BREAKTHROUGH_REPORT' && p.status === 'DRAFT';

/**
 * A DRAFT FUND_INVESTMENT/PROTECTION proposal born from the quote-backed
 * builder (has a `sourceQuoteId`) — mirrors `isBreakthroughDraft`'s
 * direct-resume pattern (Investment & Protection Proposals PRD IP-3): these
 * two types have no v1 wizard, so the row resumes straight into their
 * builder instead of the generic detail page.
 */
const isQuoteBuilderDraft = (p: Proposal): boolean =>
  (p.proposalType === 'FUND_INVESTMENT' || p.proposalType === 'PROTECTION') &&
  p.status === 'DRAFT' &&
  Boolean(p.sourceQuoteId);

/** Builder route segment for {@link isQuoteBuilderDraft} rows. */
const QUOTE_BUILDER_ROUTE_SEGMENT: Partial<Record<ProposalType, string>> = {
  FUND_INVESTMENT: 'investment-builder',
  PROTECTION: 'protection-builder',
};

export interface ProposalsTabProps {
  profileId: string;
  brokerageId: string;
  clients: Client[];
  onCountChange?: (count: number) => void;
}

/**
 * Proposals tab on the relationship page — mirrors the Requests tab's table
 * card layout (LOATableCard + SearchBar + header actions) so the two tabs
 * read identically. Rows open the detail page (or resume the pension wizard
 * for wizard drafts); the "New Proposal" chooser launches the wizard or the
 * flat compose modals.
 */
export const ProposalsTab: React.FC<ProposalsTabProps> = ({
  profileId,
  brokerageId,
  clients,
  onCountChange,
}) => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ProposalType | ''>('');
  const [modalType, setModalType] = useState<ProposalType | null>(null);
  const [pensionWizardEnabled, setPensionWizardEnabled] = useState(false);
  const [pensionBuilderEnabled, setPensionBuilderEnabled] = useState(false);
  const [investmentBuilderEnabled, setInvestmentBuilderEnabled] = useState(false);
  const [protectionBuilderEnabled, setProtectionBuilderEnabled] = useState(false);
  const [breakthroughReportEnabled, setBreakthroughReportEnabled] = useState(false);
  // Proposal targeted by the delete action (DRAFT rows only); null = closed.
  const [deleteTarget, setDeleteTarget] = useState<Proposal | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Ask the backend once per mount whether the pension wizard is active for
  // this brokerage; default to false (flat compose modal) on any failure or
  // when the endpoint isn't available yet.
  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => proposalApi.pensionWizardEnabled?.())
      .then((enabled) => {
        if (!cancelled) setPensionWizardEnabled(Boolean(enabled));
      })
      .catch(() => {
        if (!cancelled) setPensionWizardEnabled(false);
      });
    // Sectioned Pension Proposal Builder v2 — when on, "New Proposal →
    // Pension" starts on the builder's Quote Request rail section instead of
    // the standalone wizard. Same any-rejection-is-disabled contract.
    Promise.resolve()
      .then(() => proposalApi.pensionBuilderEnabled?.())
      .then((enabled) => {
        if (!cancelled) setPensionBuilderEnabled(Boolean(enabled));
      })
      .catch(() => {
        if (!cancelled) setPensionBuilderEnabled(false);
      });
    // Breakthrough Report — a whole-relationship advice report composed on
    // its own sectioned builder. Same any-rejection-is-disabled contract.
    Promise.resolve()
      .then(() => proposalApi.breakthroughReportEnabled?.())
      .then((enabled) => {
        if (!cancelled) setBreakthroughReportEnabled(Boolean(enabled));
      })
      .catch(() => {
        if (!cancelled) setBreakthroughReportEnabled(false);
      });
    // Investment / Protection quote-backed builders — independent flags
    // (Investment & Protection Proposals PRD RQ-7). Same
    // any-rejection-is-disabled contract; off falls back to the flat
    // compose modal for that type.
    Promise.resolve()
      .then(() => proposalApi.investmentBuilderEnabled?.())
      .then((enabled) => {
        if (!cancelled) setInvestmentBuilderEnabled(Boolean(enabled));
      })
      .catch(() => {
        if (!cancelled) setInvestmentBuilderEnabled(false);
      });
    Promise.resolve()
      .then(() => proposalApi.protectionBuilderEnabled?.())
      .then((enabled) => {
        if (!cancelled) setProtectionBuilderEnabled(Boolean(enabled));
      })
      .catch(() => {
        if (!cancelled) setProtectionBuilderEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadProposals = useCallback(() => {
    setLoading(true);
    setError(null);
    proposalApi
      .listProposals(profileId, { size: 100 })
      .then((page) => {
        setProposals(page.content);
        onCountChange?.(page.totalElements);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load proposals'))
      .finally(() => setLoading(false));
  }, [profileId, onCountChange]);

  useEffect(() => {
    loadProposals();
  }, [loadProposals]);

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of clients) {
      map.set(c.id, [c.firstName, c.lastName].filter(Boolean).join(' '));
    }
    return map;
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return proposals.filter((p) => {
      if (typeFilter && p.proposalType !== typeFilter) return false;
      if (!q) return true;
      const haystack = [
        p.proposalNumber,
        p.title ?? '',
        proposalTypeLabel(p.proposalType),
        clientNameById.get(p.targetClientId) ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [proposals, search, typeFilter, clientNameById]);

  const handleCreated = () => {
    setModalType(null);
    loadProposals();
  };

  const handleNewProposal = (type: ProposalType) => {
    if (type === 'PENSION' && pensionBuilderEnabled) {
      navigate(`/relationships/${profileId}/proposals/pension-builder`);
      return;
    }
    if (type === 'PENSION' && pensionWizardEnabled) {
      navigate(`/relationships/${profileId}/proposals/pension-wizard`);
      return;
    }
    if (type === 'FUND_INVESTMENT' && investmentBuilderEnabled) {
      navigate(`/relationships/${profileId}/proposals/investment-builder`);
      return;
    }
    if (type === 'PROTECTION' && protectionBuilderEnabled) {
      navigate(`/relationships/${profileId}/proposals/protection-builder`);
      return;
    }
    if (type === 'BREAKTHROUGH_REPORT') {
      // No product data to capture up front and no "who/what" dialog — mirror
      // the pension builder: create the DRAFT for the relationship's primary
      // client (the server fills a brokerage-branded title/message) and drop
      // the advisor straight into the sectioned builder.
      const target = clients.find((c) => c.isPrimary) ?? clients[0];
      if (!target) {
        setError('Add a client to this relationship before creating a Breakthrough Report.');
        return;
      }
      void (async () => {
        try {
          const created = await proposalApi.createProposal({
            profileId,
            targetClientId: target.id,
            proposalType: 'BREAKTHROUGH_REPORT',
            details: {},
          });
          navigate(`/relationships/${profileId}/proposals/breakthrough-builder/${created.id}`);
        } catch (e) {
          setError(e instanceof Error ? e.message : 'Failed to create the Breakthrough Report');
        }
      })();
      return;
    }
    setModalType(type);
  };

  const handleRowClick = useCallback(
    (p: Proposal) => {
      if (isWizardDraft(p)) {
        navigate(`/relationships/${profileId}/proposals/pension-wizard/${p.id}`);
        return;
      }
      if (isBreakthroughDraft(p)) {
        navigate(`/relationships/${profileId}/proposals/breakthrough-builder/${p.id}`);
        return;
      }
      if (isQuoteBuilderDraft(p)) {
        const segment = QUOTE_BUILDER_ROUTE_SEGMENT[p.proposalType];
        if (segment) {
          navigate(`/relationships/${profileId}/proposals/${segment}/${p.id}`);
          return;
        }
      }
      navigate(`/relationships/${profileId}/proposals/${p.id}`);
    },
    [navigate, profileId],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await proposalApi.deleteProposal(deleteTarget.id);
      setDeleteTarget(null);
      loadProposals();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete proposal');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, loadProposals]);

  const columns = useMemo<LOATableColumn<Proposal>[]>(
    () => [
      {
        key: 'client',
        label: 'Client',
        render: (p) => <span>{clientNameById.get(p.targetClientId) || '—'}</span>,
      },
      {
        key: 'number',
        label: 'Number',
        render: (p) => <span className="policy-number">{p.proposalNumber}</span>,
      },
      {
        key: 'title',
        label: 'Title',
        render: (p) => (
          <span>
            {p.title ?? '—'}
            {isWizardDraft(p) && (
              <span
                data-testid={`proposal-wizard-chip-${p.id}`}
                className="proposals-table__wizard-chip"
              >
                Wizard draft
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'lastUpdated',
        label: 'Last updated',
        render: (p) => (
          <span className="loa-created-date">{formatLastUpdated(p.updatedAt ?? p.createdAt)}</span>
        ),
      },
      {
        key: 'type',
        label: 'Proposal type',
        render: (p) => (
          <Badge color="purple" data-testid={`proposal-type-badge-${p.id}`}>
            {proposalTypeLabel(p.proposalType)}
          </Badge>
        ),
        filter: {
          value: typeFilter,
          ariaLabel: 'Filter by proposal type',
          options: [
            { value: '', label: 'All types' },
            { value: 'PENSION', label: 'Pension' },
            { value: 'FUND_INVESTMENT', label: 'Investment' },
            { value: 'PROTECTION', label: 'Protection' },
          ],
          onChange: (v) => setTypeFilter(v as ProposalType | ''),
        },
      },
      {
        key: 'status',
        label: 'Status',
        render: (p) => {
          const { label, color } = statusDisplay(p.status);
          return (
            <Badge color={color} data-testid={`proposal-status-badge-${p.id}`}>
              {label}
            </Badge>
          );
        },
      },
    ],
    [typeFilter, clientNameById],
  );

  const actions = useMemo<LOATableAction<Proposal>[]>(
    () => [
      {
        key: 'view',
        icon: <EyeIcon />,
        label: 'View proposal',
        onClick: handleRowClick,
      },
      {
        key: 'delete',
        icon: <TrashIcon />,
        label: 'Delete draft',
        onClick: (p) => setDeleteTarget(p),
        visible: (p) => p.status === 'DRAFT',
      },
    ],
    [handleRowClick],
  );

  return (
    <div data-testid="proposals-tab">
      <div className="proposals-table">
        <LOATableCard
          data={filtered}
          columns={columns}
          actions={actions}
          keyExtractor={(p) => p.id}
          rowTestId={(p) => `proposal-row-${p.id}`}
          onRowClick={handleRowClick}
          loading={loading}
          error={error}
          onRetry={() => loadProposals()}
          title="Proposals"
          aboveTable={
            <div className="flex items-center justify-between gap-md px-md pb-lg">
              <div className="proposals-table__search">
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  placeholder="Search by number, title, type or client name"
                />
              </div>
              <div className="flex items-center gap-md">
                <NewProposalButton
                  onSelect={handleNewProposal}
                  breakthroughReportEnabled={breakthroughReportEnabled}
                />
              </div>
            </div>
          }
          emptyMessage="No proposals yet"
          emptyFilteredMessage="No proposals match the current filters. Clear the search or type filter to see all proposals."
        />
      </div>

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        size="medium"
        variant="warning"
      >
        <Modal.Header
          icon="trash"
          title="Delete draft proposal"
          onClose={() => setDeleteTarget(null)}
        />
        <Modal.Content>
          <p data-testid="delete-proposal-copy">
            This permanently deletes draft {deleteTarget?.proposalNumber}
            {deleteTarget && isWizardDraft(deleteTarget)
              ? ' and voids its linked quote'
              : ''}
            .
          </p>
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{
            label: 'Cancel',
            onClick: () => setDeleteTarget(null),
            disabled: deleting,
          }}
          primaryAction={{
            label: 'Delete',
            colour: 'red',
            onClick: () => {
              void handleDeleteConfirm();
            },
            disabled: deleting,
            loading: deleting,
          }}
        />
      </Modal>

      <NewPensionProposalModal
        open={modalType === 'PENSION'}
        onClose={() => setModalType(null)}
        clients={clients}
        brokerageId={brokerageId}
        profileId={profileId}
        onCreated={handleCreated}
      />
      <NewFundInvestmentProposalModal
        open={modalType === 'FUND_INVESTMENT'}
        onClose={() => setModalType(null)}
        clients={clients}
        brokerageId={brokerageId}
        profileId={profileId}
        onCreated={handleCreated}
      />
      <NewProtectionProposalModal
        open={modalType === 'PROTECTION'}
        onClose={() => setModalType(null)}
        clients={clients}
        brokerageId={brokerageId}
        profileId={profileId}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default ProposalsTab;
